import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../app/store/store';
import {
  fetchTicketTypes,
  createTicketType,
  updateTicketType,
  deleteTicketType,
} from '../../../entities/helpdesk-settings/model';
import type { TicketType } from '../../../types/helpdeskSettingsInterfaces';

/* ── Модалка создания/редактирования типа ── */
interface TypeModalProps {
  type?: TicketType | null;
  onClose: () => void;
  onSave: (data: Partial<TicketType>) => void;
  onDelete?: (id: number) => void;
}

const TypeModal: React.FC<TypeModalProps> = ({ type, onClose, onSave, onDelete }) => {
  const [name, setName] = useState(type?.name || '');
  const [description, setDescription] = useState(type?.description || '');
  const [isActive, setIsActive] = useState(type?.is_active ?? true);
  const [isDefault, setIsDefault] = useState(type?.is_default ?? false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      description: description.trim(),
      is_active: isActive,
      is_default: isDefault,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-[#2d2c2c]">
            {type ? 'Редактировать тип' : 'Добавить тип заявки'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Название <span className="text-red-500">*</span>
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например: Анализ проблемы"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Описание</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Когда использовать этот тип (видно только сотрудникам)"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Статус</label>
            <select
              value={isActive ? 'active' : 'inactive'}
              onChange={(e) => setIsActive(e.target.value === 'active')}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400"
            >
              <option value="active">Активен</option>
              <option value="inactive">Неактивен</option>
            </select>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="w-4 h-4 border border-gray-300 rounded focus:ring-2 focus:ring-red-400"
            />
            <span className="text-sm text-gray-700">
              Тип по умолчанию (ставится новым заявкам)
            </span>
          </label>

          <div className="flex items-center justify-between pt-2">
            {type && onDelete ? (
              <button
                type="button"
                onClick={() => {
                  if (confirmDelete) {
                    onDelete(type.id);
                  } else {
                    setConfirmDelete(true);
                  }
                }}
                onBlur={() => setConfirmDelete(false)}
                className={`text-sm font-medium transition-colors ${
                  confirmDelete
                    ? 'text-white bg-[#e3282a] px-3 py-2 rounded-lg hover:bg-[#c82022]'
                    : 'text-gray-400 hover:text-[#e3282a]'
                }`}
              >
                {confirmDelete ? 'Точно удалить?' : 'Удалить'}
              </button>
            ) : (
              <span />
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Отмена
              </button>
              <button
                type="submit"
                disabled={!name.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] disabled:opacity-50"
              >
                {type ? 'Сохранить' : 'Добавить'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Основной экран ── */
const TicketTypeSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { ticketTypes, ticketTypesStatus, error } = useSelector(
    (state: RootState) => state.helpdeskSettings,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<TicketType | null>(null);

  useEffect(() => {
    if (ticketTypesStatus === 'idle') dispatch(fetchTicketTypes());
  }, [dispatch, ticketTypesStatus]);

  const handleSave = useCallback(
    async (data: Partial<TicketType>) => {
      if (editingType) {
        await dispatch(updateTicketType({ id: editingType.id, data }));
      } else {
        await dispatch(createTicketType(data));
      }
      dispatch(fetchTicketTypes());
      setModalOpen(false);
      setEditingType(null);
    },
    [dispatch, editingType],
  );

  const handleDelete = useCallback(
    async (id: number) => {
      await dispatch(deleteTicketType(id));
      setModalOpen(false);
      setEditingType(null);
    },
    [dispatch],
  );

  if (ticketTypesStatus === 'loading' && ticketTypes.length === 0) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-1/3"></div>
          <div className="h-16 bg-gray-100 rounded"></div>
          <div className="h-16 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          Типы используются в условиях SLA-политик и при создании заявки
        </p>
        <button
          onClick={() => { setEditingType(null); setModalOpen(true); }}
          className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] transition-colors"
        >
          + Добавить тип
        </button>
      </div>

      {/* Ошибка удаления (тип используется в заявках) и прочие ошибки CRUD */}
      {error && (
        <p className="mb-3 text-xs text-red-500">{error}</p>
      )}

      {ticketTypes.length > 0 ? (
        <div>
          {ticketTypes.map((t) => (
            <div
              key={t.id}
              onClick={() => { setEditingType(t); setModalOpen(true); }}
              className="flex items-center justify-between py-3.5 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded transition-colors"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#2d2c2c]">{t.name}</p>
                {t.description && (
                  <p className="text-xs text-gray-400 truncate">{t.description}</p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                {t.is_default && (
                  <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-blue-50 text-blue-600 border border-blue-200">
                    По умолчанию
                  </span>
                )}
                <span
                  className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                    t.is_active
                      ? 'bg-green-50 text-green-600 border border-green-200'
                      : 'bg-gray-50 text-gray-500 border border-gray-200'
                  }`}
                >
                  {t.is_active ? 'Активен' : 'Неактивен'}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-400 text-center py-8">
          Типов заявок пока нет. Нажмите «Добавить тип», чтобы создать первый.
        </p>
      )}

      {modalOpen && (
        <TypeModal
          type={editingType}
          onClose={() => { setModalOpen(false); setEditingType(null); }}
          onSave={handleSave}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default TicketTypeSettings;
